import {
	Badge,
	Box,
	Button,
	Component,
	Icons,
	Inline,
	Stack,
	Table,
	TableDataCell,
	TableHeaderDataCell,
	Text,
} from "@sembark-travel/ui-base"
import { Dialog } from "@sembark-travel/ui-dialog"
import {
	dateToUTCString,
	endOf,
	formatDate,
	startOf,
	utcTimestampToLocalDate,
} from "@sembark-travel/datetime-utils"
import pluralize from "pluralize"
import React, { useMemo } from "react"
import { AuthLogs } from "../../AuthLogs"
import { TDayLoginCell, buildDaywiseLogins } from "./utils"

/**
 * A user's month, as the billing details and the monthly users report list
 * them.
 */
export type TMonthlyUser = {
	id: number
	name: string
	invited_at?: string
	signup_at?: string
	deleted_at?: string
	new_signup: 0 | 1
	new_deleted: 0 | 1
	last_activity_at?: string
	/** distinct days with a login this month */
	login_days: number
	/** all successful logins this month */
	logins: number
	/** successful logins from outside India this month */
	foreign_logins: number
	/** failed logins (password or 2FA) from outside India this month */
	foreign_failed_logins: number
}

export type TMonthlyLogin = {
	user_id: number
	country_code?: string | null
	created_at: string
}

/**
 * Users of a month with their logins, outside-India logins and a cell per
 * day (newest first). User ID and Name stay in view while the days scroll.
 * The tenant is shown when given, and short-lived accounts are marked next
 * to the name when their days in the month are given.
 */
export function MonthlyUsersTable<TUser extends TMonthlyUser>({
	month,
	users,
	logins,
	renderTenant,
	daysInMonth,
	hideSerialAndId,
	hideTotals,
}: {
	month: Date
	users: Array<TUser>
	logins: Array<TMonthlyLogin>
	renderTenant?: (user: TUser) => React.ReactNode
	/** days the account existed in the month, to mark short-lived ones */
	daysInMonth?: (user: TUser) => number | null
	/** leave out S.N. and User ID; Name alone stays in view */
	hideSerialAndId?: boolean
	/** leave out the Total row, e.g. when users are paged */
	hideTotals?: boolean
}) {
	const daywise = useMemo(
		() => buildDaywiseLogins({ month, users, logins, newestFirst: true }),
		[month, users, logins]
	)
	const withId = !hideSerialAndId
	const headers = [
		...(withId ? ["S.N.", "User ID"] : []),
		"Name",
		...(renderTenant ? ["Tenant"] : []),
		"Logins",
	]
	return (
		<Stack gap="2">
			<DayLoginLegend />
			<Table
				bordered
				responsive
				stickyHeader
				// the header can only stick inside a box that scrolls
				// vertically, so cap the height of the scroll wrapper
				style={{ maxHeight: "70vh", overflowY: "auto" }}
			>
				<thead>
					<tr>
						{headers.map((header, column) => (
							<TableHeaderDataCell
								key={header}
								{...stickyUserColumn(column, withId, true)}
							>
								{header}
							</TableHeaderDataCell>
						))}
						<TableHeaderDataCell
							title="Logins Outside India"
							borderRightWidth="2"
						>
							Outside
						</TableHeaderDataCell>
						{daywise.days.map((date, index) => (
							<TableHeaderDataCell key={index} textAlign="center">
								<DayHeader date={date} />
							</TableHeaderDataCell>
						))}
					</tr>
				</thead>
				<tbody>
					{!hideTotals ? (
						<tr>
							{withId ? (
								<>
									<TableDataCell />
									<TableDataCell {...stickyUserColumn(1, withId)} />
								</>
							) : null}
							<TableDataCell
								fontWeight="semibold"
								{...stickyUserColumn(withId ? 2 : 0, withId)}
							>
								Total
							</TableDataCell>
							{renderTenant ? <TableDataCell /> : null}
							<TableDataCell
								fontWeight="semibold"
								title="Users who logged in this month"
							>
								{users.filter((u) => u.login_days).length}
							</TableDataCell>
							<TableDataCell
								fontWeight="semibold"
								color="warning"
								borderRightWidth="2"
							>
								{collectSum(users, (u) => u.foreign_logins) || "-"}
							</TableDataCell>
							{daywise.totals.map((count, index) => (
								<TableDataCell
									key={index}
									textAlign="center"
									fontWeight="semibold"
									color={count ? "success" : "muted"}
									title="Users who logged in"
								>
									{count}
								</TableDataCell>
							))}
						</tr>
					) : null}
					{users.map((u, index) => {
						const days = daysInMonth?.(u)
						const columns = [
							...(withId
								? [<Text>{index + 1}</Text>, <Text>{u.id}</Text>]
								: []),
							<UserNameWithStatus
								user={u}
								signedUpThisMonth={Boolean(u.new_signup)}
								deletedThisMonth={Boolean(u.new_deleted)}
								shortLivedDays={
									// null when not signed up: listed, never short-lived
									typeof days === "number" && days <= SHORT_LIFESPAN_DAYS
										? days
										: undefined
								}
								month={month}
							/>,
							...(renderTenant ? [renderTenant(u)] : []),
							u.login_days ? (
								<Component initialState={false}>
									{({ state, setState }) => (
										<>
											<Button
												inline
												title={[
													`Logged in on ${pluralize("day", u.login_days, true)}`,
													pluralize("login", u.logins, true),
													u.last_activity_at
														? `last on ${formatDate(
																utcTimestampToLocalDate(u.last_activity_at),
																"DD MMM, hh:mm A"
															)}`
														: null,
												]
													.filter(Boolean)
													.join(" • ")}
												onClick={() => setState(true)}
											>
												{u.login_days}
											</Button>
											<Dialog
												open={state}
												onClose={() => setState(false)}
												title={`${u.name}: Security Logs in ${formatDate(month, "MMM YYYY")}`}
												xl
											>
												<Dialog.Body>
													<AuthLogs
														userId={u.id}
														filters={{
															created_after: dateToUTCString(
																startOf(month, "month")
															),
															created_before: dateToUTCString(
																endOf(month, "month")
															),
														}}
													/>
												</Dialog.Body>
											</Dialog>
										</>
									)}
								</Component>
							) : (
								"-"
							),
						]
						return (
							<tr key={u.id}>
								{/* (S.N., User ID), Name, (Tenant), Logins */}
								{columns.map((content, column) => (
									<TableDataCell
										key={column}
										{...stickyUserColumn(column, withId)}
									>
										{content}
									</TableDataCell>
								))}
								{/* Logins Outside India, set apart from the days */}
								<TableDataCell borderRightWidth="2">
									<ForeignLogins user={u} month={month} />
								</TableDataCell>
								{(daywise.cellsByUser[u.id] || []).map((cell, day) => (
									<DayLoginTableCell
										key={day}
										cell={cell}
										inactive={!u.signup_at}
									/>
								))}
							</tr>
						)
					})}
				</tbody>
			</Table>
		</Stack>
	)
}

/**
 * Logins (and failed attempts) from outside India, opening those auth logs.
 */
function ForeignLogins({ user, month }: { user: TMonthlyUser; month: Date }) {
	if (!user.foreign_logins && !user.foreign_failed_logins) {
		return <>-</>
	}
	return (
		<Component initialState={false}>
			{({ state, setState }) => (
				<>
					<Button
						inline
						title="See login attempts from outside India this month"
						onClick={() => setState(true)}
					>
						<Text as="span" color="warning" fontWeight="semibold">
							{user.foreign_logins}
							{user.foreign_failed_logins ? (
								<Text as="span" fontSize="sm" fontWeight="normal">
									{" "}
									(+{user.foreign_failed_logins} failed)
								</Text>
							) : null}
						</Text>
					</Button>
					<Dialog
						open={state}
						onClose={() => setState(false)}
						title={`${user.name}: Logins Outside India in ${formatDate(month, "MMM YYYY")}`}
						xl
					>
						<Dialog.Body>
							<AuthLogs
								userId={user.id}
								filters={{
									events: ["login", "login_failed", "mfa_failed"],
									exclude_country: "IN",
									created_after: dateToUTCString(startOf(month, "month")),
									created_before: dateToUTCString(endOf(month, "month")),
								}}
							/>
						</Dialog.Body>
					</Dialog>
				</>
			)}
		</Component>
	)
}

/**
 * Accounts present for at most this many days of a month are short-lived,
 * as in the monthly reports.
 */
const SHORT_LIFESPAN_DAYS = 15

/**
 * The user's name with their status: a warning user icon when they have not
 * signed up (status in its title), "+ 03 Sep" / "x 05 Sep" when they joined
 * or were deleted this month, and "11 days" when the account was
 * short-lived in the month.
 */
export function UserNameWithStatus({
	user,
	signedUpThisMonth,
	deletedThisMonth,
	shortLivedDays,
	month,
}: {
	user: {
		name: string
		signup_at?: string
		invited_at?: string
		deleted_at?: string
	}
	signedUpThisMonth?: boolean
	deletedThisMonth?: boolean
	/** days in the month, given only for a short-lived account */
	shortLivedDays?: number
	month?: Date
}) {
	const day = (timestamp: string, format = "DD MMM") =>
		formatDate(utcTimestampToLocalDate(timestamp), format)
	return (
		<Inline gap="1" alignItems="center" whiteSpace="nowrap">
			{/* long names are cut off at 150px; the full name shows on hover */}
			<Text
				title={user.name}
				style={{
					maxWidth: "150px",
					overflow: "hidden",
					textOverflow: "ellipsis",
				}}
			>
				{user.name}
			</Text>
			{!user.signup_at ? (
				<Text
					as="span"
					color="warning"
					title={
						user.invited_at
							? `Not signed up (invited on ${day(user.invited_at, "DD MMM YYYY")})`
							: "Not signed up"
					}
				>
					<Icons.User />
				</Text>
			) : null}
			{signedUpThisMonth && user.signup_at ? (
				<Badge
					success
					title={`Signed up on ${day(user.signup_at, "DD MMM YYYY")}`}
				>
					+ {day(user.signup_at)}
				</Badge>
			) : null}
			{deletedThisMonth && user.deleted_at ? (
				<Badge
					warning
					title={`Deleted on ${day(user.deleted_at, "DD MMM YYYY")}`}
				>
					x {day(user.deleted_at)}
				</Badge>
			) : null}
			{shortLivedDays !== undefined ? (
				<Badge
					warning
					title={`Short-lived: present for ${pluralize("day", shortLivedDays, true)}${
						month ? ` of ${formatDate(month, "MMM YYYY")}` : ""
					} (${SHORT_LIFESPAN_DAYS} or fewer)`}
				>
					<Icons.Attention /> {pluralize("day", shortLivedDays, true)}
				</Badge>
			) : null}
		</Inline>
	)
}

const STICKY_USER_ID_WIDTH = 80

/**
 * Keeps the User ID (column 1) and Name (column 2) in view while the day
 * columns scroll. User ID has a fixed width so Name knows where to stick;
 * S.N. scrolls away beneath them. Without S.N. and User ID, Name is column
 * 0 and sticks to the left edge.
 */
function stickyUserColumn(column: number, withId: boolean, isHeader = false) {
	const isId = withId && column === 1
	const isName = column === (withId ? 2 : 0)
	if (!isId && !isName) {
		return {}
	}
	return {
		bgColor: "default" as const,
		style: {
			position: "sticky" as const,
			left: isName && withId ? STICKY_USER_ID_WIDTH : 0,
			zIndex: isHeader ? 4 : 2,
			...(isId
				? {
						width: STICKY_USER_ID_WIDTH,
						minWidth: STICKY_USER_ID_WIDTH,
						maxWidth: STICKY_USER_ID_WIDTH,
					}
				: { borderRightWidth: "2px" }),
		},
	}
}

function collectSum<T>(items: Array<T>, value: (item: T) => number): number {
	return items.reduce((sum, item) => sum + (value(item) || 0), 0)
}

function isSunday(date: Date) {
	return formatDate(date, "dddd").toLowerCase() === "sunday"
}

/**
 * Day of month header for the day-wise columns; Sundays highlighted.
 */
export function DayHeader({ date }: { date: Date }) {
	const isHoliday = isSunday(date)
	return (
		<Box
			as="span"
			bgColor={isHoliday ? "danger_emphasis" : undefined}
			color={isHoliday ? "on_emphasis" : undefined}
			title={isHoliday ? "Holiday" : formatDate(date, "ddd, DD MMM")}
		>
			{formatDate(date, "DD")}
		</Box>
	)
}

/**
 * What the fills and symbols of the day-wise cells mean.
 */
export function DayLoginLegend() {
	return (
		<Inline gap="4" flexWrap="wrap" fontSize="sm">
			{(
				[
					["success", "✓", "success", "Logged in"],
					[
						"warning",
						"SG",
						"warning",
						"Logged in from outside India (country)",
					],
					["primary", "+", "accent", "Signed up"],
					["danger", "×", "danger", "Deleted"],
					[undefined, "·", "muted", "No login"],
					["inset", "", undefined, "Account not active"],
				] as const
			).map(([bgColor, symbol, color, label]) => (
				<Inline key={label} gap="1" alignItems="center">
					<Box
						bgColor={bgColor}
						borderWidth="1"
						paddingX="1"
						textAlign="center"
						style={{ minWidth: "28px" }}
					>
						<Text as="span" color={color} fontWeight="semibold">
							{symbol || " "}
						</Text>
					</Box>
					<Text>{label}</Text>
				</Inline>
			))}
		</Inline>
	)
}

/**
 * One user's day in the day-wise logins table, filled by what happened:
 * green for a login (country codes on orange when outside India), blue for
 * a signup, red for a deletion, grey when the account was not active.
 */
export function DayLoginTableCell({
	cell,
	inactive,
}: {
	cell: TDayLoginCell
	/** e.g. invited but never signed up */
	inactive?: boolean
}) {
	if (inactive || cell.outside_account) {
		return (
			<TableDataCell bgColor="inset" title="Account not active">
				{null}
			</TableDataCell>
		)
	}
	const bgColor = cell.deleted
		? "danger"
		: cell.logins
			? cell.foreign_countries.length
				? "warning"
				: "success"
			: cell.signed_up
				? "primary"
				: undefined
	return (
		<TableDataCell textAlign="center" bgColor={bgColor}>
			<DayLoginCellView cell={cell} />
		</TableDataCell>
	)
}

/**
 * ✓ (or the countries when outside India) for logins, + for signup, × for
 * deletion, · for none.
 */
function DayLoginCellView({ cell }: { cell: TDayLoginCell }) {
	const title = [
		cell.signed_up ? "Signed up" : null,
		cell.logins
			? `${pluralize("login", cell.logins, true)}${
					cell.foreign_countries.length
						? ` (outside India: ${cell.foreign_countries.join(", ")})`
						: ""
				}`
			: null,
		cell.deleted ? "Deleted" : null,
	]
		.filter(Boolean)
		.join(" • ")
	if (!cell.signed_up && !cell.logins && !cell.deleted) {
		return (
			<Text as="span" color="muted" title="No login">
				·
			</Text>
		)
	}
	return (
		<Inline gap="px" justifyContent="center" title={title} whiteSpace="nowrap">
			{cell.signed_up ? (
				<Text as="span" color="accent" fontWeight="semibold">
					+
				</Text>
			) : null}
			{cell.logins ? (
				cell.foreign_countries.length ? (
					<Text as="span" color="warning" fontWeight="semibold" fontSize="sm">
						{cell.foreign_countries.join(",")}
					</Text>
				) : (
					<Text as="span" color="success" fontWeight="semibold">
						✓
					</Text>
				)
			) : null}
			{cell.deleted ? (
				<Text as="span" color="danger" fontWeight="semibold">
					×
				</Text>
			) : null}
		</Inline>
	)
}
